import { useState, useRef } from "react"
import styles from "./FeedCard.module.scss"
import { connect } from "react-redux"
import {
  likePost,
  createPostComment,
  deletePost,
  openMainModal,
  editPosteo,
  getPostComments,
} from "../../../store/actions"
import { ReactComponent as MundoPost } from "../../../assets/svg/worldPost.svg"
import { ReactComponent as DotPost } from "../../../assets/svg/dotPost.svg"
import { ReactComponent as PencilIcon } from "../../../assets/svg/editComment.svg"
import { useNavigate } from "react-router-dom"
import TextareaAutosize from "react-textarea-autosize"
import useUserImage from "../../../hooks/useUserImage"
import { ReactComponent as NoImage } from "../../../assets/svg/noImageIcon.svg"
import FeedCardComment from "./FeedCardComment"
import FeedCardUsersReacted from "./FeedCardUsersReacted"
import { Check, X, Heart, MessageSquare, ChevronDown } from "lucide-react"

const FeedCard = ({
  userData,
  posteos,
  likePost,
  createPostComment,
  deletePost,
  deletePostComment,
  editPostComment,
  getPostComments,
  editPosteo,
  openMainModal,
  myPosts,
  item,
  ...props
}) => {
  const [isEditingPost, setIsEditingPost] = useState(false)
  const [editPost, setEditPost] = useState("")
  const [editImage, setEditImage] = useState("")
  const [editImagePreview, setEditImagePreview] = useState("")
  const [editVideo, setEditVideo] = useState("")
  const [editVideoPreview, setEditVideoPreview] = useState("")
  const [showMenu, setShowMenu] = useState(false)
  const [showUsersReacted, setShowUsersReacted] = useState(false)
  const [showComments, setShowComments] = useState(false)
  const [heartAnimation, setHeartAnimation] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const [commentsLoaded, setCommentsLoaded] = useState(false)

  const navigate = useNavigate()
  const commentsLoadedRef = useRef(false)
  const cardRef = useRef(null)

  const { content, comments, id, createdAt, video, images } = item
  const { name, last_name, razon_social, type, id: userId } = item.user
  const likesArray = item.likes || []
  const isMyLike = likesArray.some((like) => like?.user.id === userData?.id)
  const isMyPost = item?.user?.id === userData?.id
  const hasImage = item.images[0]?.image ? true : false
  const hasVideo = item.video[0]?.video ? true : false
  const completeName = type === "COMPANY" ? razon_social : name + " " + last_name
  const { imageError, initials, profileImage } = useUserImage(item.user)

  //Maneja el redireccionamiento al perfil del usuario
  const handleProfile = (id, completeName) => {
    navigate(`/perfil/${id}`)
  }

  const handleLike = (postId) => {
    setHeartAnimation(true)
    likePost(postId)
    setTimeout(() => {
      setHeartAnimation(false)
    }, 2000)
  }

  const loadComments = () => {
    if (!commentsLoadedRef.current && id) {
      commentsLoadedRef.current = true
      setCommentsLoaded(true)
      getPostComments(id)
    }
  }

  const calcularDiferenciaTiempo = (fechaCreacion) => {
    const fechaActual = new Date()
    const fechaCreacionPost = new Date(fechaCreacion)
    const diferencia = fechaActual - fechaCreacionPost

    const minutos = Math.floor(diferencia / (1000 * 60))
    const horas = Math.floor(diferencia / (1000 * 60 * 60))
    const dias = Math.floor(diferencia / (1000 * 60 * 60 * 24))
    const semanas = Math.floor(diferencia / (dias * 7))
    const meses = Math.floor(dias / 30)

    if (minutos === 0) {
      return "Ahora"
    } else if (minutos === 1) {
      return "1 minuto"
    } else if (minutos < 60) {
      return `${minutos} minutos`
    } else if (horas === 1) {
      return "1 hora"
    } else if (horas < 24) {
      return `${horas} horas`
    } else if (dias === 1) {
      return "1 día"
    } else if (dias < 7) {
      return `${dias} días`
    } else if (semanas === 1) {
      return "1 semana"
    } else if (semanas < 4) {
      return `${semanas} semanas`
    } else if (meses === 1) {
      return "1 mes"
    } else {
      return `${meses} meses`
    }
  }

  //Maneja el renderizado de los comentarios
  const handleShowPostComments = (id) => {
    const currentScrollY = window.scrollY
    const cardTop = cardRef.current?.offsetTop || 0

    if (showComments === id) {
      setShowComments(false)
    } else {
      setShowComments(id)
      loadComments()

      setTimeout(() => {
        const newCardTop = cardRef.current?.offsetTop || 0
        const scrollDifference = newCardTop - cardTop
        window.scrollTo(0, currentScrollY + scrollDifference)
      }, 100)
    }
  }

  //Manejea el menu de opciones del posteo
  const handleToggleMenu = () => {
    setShowMenu(!showMenu)
  }

  //Funcion para eliminar un posteo
  const handleDeletePost = (id) => {
    openMainModal({
      title: "¿Estas seguro de querer eliminar el posteo?",
      body: "Si lo eliminas no podrás recuperarlo",
      isSuccess: false,
      iconType: "warning",
      confirmHandler: () => {
        deletePost(id)
      },
    })
  }

  //Funcion para editar un posteo
  const handleEditPost = (postId, images, content, video) => {
    const body = {
      id: postId,
      images: images,
      content: content,
      video: video,
    }
    editPosteo(body) 
    setIsEditingPost(false)
    setEditPost("")
    setEditImage("")
    setEditImagePreview("")
    setEditVideo("")
    setEditVideoPreview("")
  }

  const handleCancelEditPost = () => {
    setIsEditingPost(false)
    setEditPost("")
    setEditImage("")
    setEditImagePreview("")
    setEditVideo("")
    setEditVideoPreview("")
    setShowMenu(false)
  }

  //Maneja la selección de la opción del menu
  const handleMenuItemClick = (action, id, postMenu) => {
    setShowMenu(false)
    if (action === "Eliminar") {
      handleDeletePost(id)
    }
    if (action === "Editar") {
      setIsEditingPost(true)
      setEditPost(content) 
    }
    if (action === "Cerrar") {
      setIsEditingPost(false)
    }
  }

  return (
    <section className={styles.generalContainer} ref={cardRef}>
      <div
        className={`${styles.feed_itemContainer} ${isHovered ? styles.hovered : ""}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className={styles.feed_itemHeader_profileContainer}>
          <div
            className={styles.feed_itemHeader_profileContainer_nameAndImg}
          >
            <div className={styles.feed_itemHeader_imgContainer} onClick={() => handleProfile(userId, completeName)}>
              {!imageError && profileImage ? (
                <img
                  className={styles.feed_itemHeader_imgContainer_img}
                  src={profileImage || "/placeholder.svg"}
                  alt="user"
                />
              ) : initials ? (
                <div className={styles.feed_itemHeader_imgContainer_initials}>{initials}</div>
              ) : (
                <div className={styles.feed_itemHeader_imgContainer_img}>{<NoImage width={60} height={60} />}</div>
              )}
            </div>
            <div className={styles.feed_itemHeader_text} onClick={() => handleProfile(userId, completeName)}>
              <div className={styles.feed_itemHeader_info}>
                <p className={styles.feed_itemHeader_text_name}>{completeName}</p>
                <div className={styles.feed_itemHeader_text_timeContainer}>
                  <MundoPost width={15} height={15} />
                  <span className={styles.feed_itemHeader_text_time}>
                    <DotPost height={10} width={10} />
                  </span>
                  {calcularDiferenciaTiempo(createdAt)}
                </div>
              </div>
            </div>
          </div>
        </div>
        {hasImage &&
          isEditingPost &&
          (editImagePreview ? (
            <div className={styles.feed_itemBody_imgContainer}>
              <img src={editImagePreview || "/placeholder.svg"} alt="" className={styles.feed_itemBody_imgPost} />
            </div>
          ) : (
            <label htmlFor="postImage" className={styles.feed_itemBody_input_item_label}>
              <input
                type="file"
                accept="image/*"
                id="postImage"
                onChange={(e) => {
                  const file = e.target.files[0]
                  if (file) {
                    setEditImagePreview(URL.createObjectURL(file))
                    setEditImage(file)
                  }
                }}
                style={{ display: "none" }}
              />
              <div className={styles.feed_itemBody_imgContainer}>
                <img src={images[0]?.image || "/placeholder.svg"} alt="" className={styles.feed_itemBody_imgPost} />
                <p className={styles.feed_itemBody_imgContainer_editButton}>Cambiar imagen</p>
              </div>
            </label>
          ))}
        {hasImage && !isEditingPost && (
          <div className={styles.feed_itemBody_imgContainer} style={{ "--bg-image": `url(${images[0]?.image})` }}>
            <img
              src={images[0]?.image || "/placeholder.svg"}
              alt={images[0]?.image}
              className={styles.feed_itemBody_imgPost}
            />
          </div>
        )}
        {hasVideo &&
          isEditingPost &&
          (editVideoPreview ? (
            <div className={styles.feed_itemBody_videoContainer}>
              <video src={editVideoPreview} controls className={styles.feed_itemBody_videoPost}></video>
            </div>
          ) : (
            <label htmlFor="postVideo" className={styles.feed_itemBody_input_item_label}>
              <input
                type="file"
                accept="video/*"
                id="postVideo"
                onChange={(e) => {
                  const file = e.target.files[0]
                  if (file) {
                    setEditVideoPreview(URL.createObjectURL(file))
                    setEditVideo(file)
                  }
                }}
                style={{ display: "none" }}
              />
              <div className={styles.feed_itemBody_videoContainer}>
                <video src={video[0]?.video} controls className={styles.feed_itemBody_videoPost}></video>
                <p className={styles.feed_itemBody_videoContainer_editButton}>Cambiar video</p>
              </div>
            </label>
          ))}
        {hasVideo && !isEditingPost && (
          <div
            className={styles.feed_itemBody_videoContainer}
            style={{backgroundColor: "rgb(0 0 0 / 80%)"}}  
          >
            <video src={video[0]?.video} controls className={styles.feed_itemBody_videoPost}></video>
          </div>
        )}

        {isEditingPost ? (
          <div className={styles.feed_itemBody_inputContainer}>
            <TextareaAutosize
              type="text"
              className={styles.feed_itemBody_input_item}
              id="postText"
              minRows={3}
              value={editPost || content}
              onChange={(e) => setEditPost(e.target.value)}
              onInput={(e) => {
                e.target.style.height = "auto"
                e.target.style.height = e.target.scrollHeight + 10 + "px"
              }}
            />
            <div className={styles.feed_itemBody_input_item_iconContainer}>
              <button
                className={styles.feed_itemBody_input_item_iconContainer_cancelBtn}
                onClick={() => handleCancelEditPost()}
              >
                <X size={16} />
                Cancelar
              </button>
              <button
                className={styles.feed_itemBody_input_item_iconContainer_sendBtn}
                onClick={() => handleEditPost(id, editImage, editPost, editVideo)}
              >
                <Check size={16} />
                Guardar
              </button>
            </div>
          </div>
        ) : (
          content && <div className={styles.feed_itemBody_txt}>{content}</div>
        )}
        <div className={styles.feed_itemHeader_footerContainer}>
          <div className={styles.feed_itemHeader_actionsMenu_menu}>
            <div
              className={`${styles.feed_itemHeader_actionsMenu_menu_item} 
                        ${heartAnimation ? styles.heartbeat : ""}
                            ${styles.heartIcon} ${isMyLike ? styles.heartIcon_active : ""}`}
              onClick={() => handleLike(id)}
              onMouseEnter={() => setShowUsersReacted(true)}
              onMouseLeave={() => setShowUsersReacted(false)}
            >
              <Heart size={20} />
              {likesArray?.length > 0 && (
                <p className={styles.feed_itemHeader_actionsMenu_menu_item_number}>{likesArray?.length}</p>
              )}
              {showUsersReacted && <FeedCardUsersReacted users={likesArray} handleProfile={handleProfile} />}
            </div>
            <div className={styles.feed_itemHeader_actionsMenu_menu_item} onClick={() => handleShowPostComments(id)}>
              <MessageSquare size={20} title="Comentar" />
              {comments?.length > 0 && (
                <p className={styles.feed_itemHeader_actionsMenu_menu_item_number}>{comments?.length}</p>
              )}
              <span className={styles.feed_itemHeader_actionsMenu_menu_item_text}>
                Comentarios
                <ChevronDown
                  size={16}
                  className={showComments === id ? styles.feed_itemHeader_actionsMenu_menu_item_iconRotated : ""}
                />
              </span>
            </div>
          </div>
          {isMyPost && (
            <div className={styles.feed_itemHeader_options}>
              <div className={styles.feed_itemHeader_options_container_item} onClick={() => handleToggleMenu()}>
                <PencilIcon width={16} height={16} title="Editar" />
              </div>
              {showMenu && (
                <ul className={styles.feed_itemHeader_options_dropdownMenu}>
                  <li className={styles.feed_itemHeader_options_dropdownMenu_close} onClick={() => handleToggleMenu()}>
                    X
                  </li>
                  <li
                    className={styles.feed_itemHeader_options_dropdownMenu_item}
                    onClick={() => handleMenuItemClick("Editar", id, true)}
                  >
                    Editar
                  </li>
                  <li
                    className={styles.feed_itemHeader_options_dropdownMenu_item}
                    onClick={() => handleMenuItemClick("Eliminar", id, true)}
                  >
                    Eliminar
                  </li>
                </ul>
              )}
            </div>
          )}
        </div>
        <FeedCardComment
          comments={commentsLoaded ? comments : []}
          userData={userData}
          showComments={showComments}
          setShowComments={setShowComments}
          id={id}
          isHovered={isHovered}
          calcularDiferenciaTiempo={calcularDiferenciaTiempo}
          commentsLoaded={commentsLoaded}
          loadComments={loadComments}
        />
      </div>
    </section>
  )
}

const mapStateToProps = (state) => {
  return {
    userData: state.general.userData,
  }
}

export default connect(mapStateToProps, {
  likePost,
  createPostComment,
  deletePost,
  openMainModal,
  editPosteo,
  getPostComments,
})(FeedCard)